import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle, Crown, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      navigate('/profile');
      return;
    }
    if (polledRef.current) return;
    polledRef.current = true;
    pollPaymentStatus(sessionId, 0);
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2500;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const { data } = await axios.get(
        `${API_URL}/api/subscription/status/${sid}`,
        { withCredentials: true }
      );

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setStatus('processing');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Status check error:', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  const tierConfig = {
    premium: { icon: Crown, color: '#F59E0B', label: 'Premium' },
    ultimate: { icon: Zap, color: '#E11D48', label: 'Ultimate' },
  };

  const tier = paymentData?.tier || 'premium';
  const config = tierConfig[tier] || tierConfig.premium;
  const TierIcon = config.icon;

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="subscription-success-page">
      <div className="max-w-sm w-full text-center">
        {(status === 'checking' || status === 'processing') && (
          <div className="space-y-4" data-testid="payment-processing">
            <div className="w-20 h-20 mx-auto rounded-full bg-white/5 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-[#E11D48] animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-white">Processing Payment...</h2>
            <p className="text-sm text-[#71717A]">Please wait while we verify your payment</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6" data-testid="payment-success">
            <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: `${config.color}20` }}>
              <CheckCircle className="w-10 h-10" style={{ color: config.color }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white font-['Unbounded'] mb-2">Welcome to {config.label}!</h2>
              <p className="text-sm text-[#A1A1AA]">Your subscription is now active</p>
            </div>
            <div className="p-4 rounded-xl border border-white/10 bg-white/5">
              <div className="flex items-center justify-center gap-2 mb-2">
                <TierIcon className="w-5 h-5" style={{ color: config.color }} />
                <span className="font-bold text-white">{config.label} Plan</span>
              </div>
              <p className="text-xs text-[#71717A]">
                {paymentData?.amount ? `£${paymentData.amount.toFixed(2)}` : ''}/month
              </p>
            </div>
            <Button
              onClick={() => navigate('/profile')}
              className="w-full rounded-full text-white"
              style={{ backgroundColor: config.color }}
              data-testid="go-to-profile-btn"
            >
              Go to Profile
            </Button>
          </div>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <div className="space-y-4" data-testid="payment-error">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#E11D48]/10 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-[#E11D48]" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
            </h2>
            <p className="text-sm text-[#71717A]">
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t verify your payment. If you were charged, please contact support.'}
            </p>
            <Button
              onClick={() => navigate('/profile')}
              className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full"
              data-testid="back-to-profile-btn"
            >
              Back to Profile
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
